import {
    IonHeader,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonMenuButton,
    IonProgressBar,
    IonBackButton,
    IonSkeletonText,
} from '@ionic/react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router'

import { ENV_NAME } from '../../lib/apollo/config'
import useTabContext from '../../lib/hooks/useTabContext'
import useWindowResizeListener from '../../lib/hooks/useWindowResizeListener'
import IS_IOS from '../../lib/isIOS'
import { useLoadingIndicator } from '../../providers/GlobalLoading/hooks'
import DesktopMenu from '../DesktopMenu'
import Hide from '../Hide'

import './style.scss'

interface HeaderProps {
    title: string | JSX.Element
    buttonLeft?: 'back' | 'menu' | 'none'
    isModal?: boolean
    /**
     * Ziel des Zurueck-Knopfes, wenn es keine Historie gibt. Ohne Angabe wird
     * `/<erstes Pfadsegment>` benutzt — das trifft nur zu, solange das erste
     * Segment auch ein Tab ist. Seiten ausserhalb der Tabs muessen es setzen.
     */
    defaultHref?: string
}

const Header: React.FC<HeaderProps & Omit<React.ComponentProps<typeof IonHeader>, 'title'>> = ({ isModal, title, buttonLeft= 'back', mode, defaultHref: defaultHrefProp, children, ...headerProps }) => {
    const { visible } = useLoadingIndicator()
    const { t } = useTranslation()
    const { path } = useRouteMatch()
    const { hideMenuButton } = useWindowResizeListener()

    const splitPath = path.split('/')
    const tab = useTabContext()
    const defaultHref =
        defaultHrefProp ?? (splitPath.length > 2 ? `/${tab}` : '/')

    return (
        <IonHeader
            {...headerProps}
            className={clsx(headerProps.className, 'header')}
        >
            {ENV_NAME === 'sandbox' && (
                <div
                    className='ribbon'
                >
                Demo
                </div>
            )}
            <IonToolbar
                className={`header__toolbar header__toolbar--${ENV_NAME}`}
            >
                <IonButtons
                    className={clsx('header__btn', { 'header__btn--hide-desktop': buttonLeft === 'menu' && hideMenuButton })}
                    slot='start'
                >
                    { buttonLeft === 'back' && (
                        <IonBackButton
                            text={IS_IOS ? t('buttons.back') : undefined}
                            defaultHref={defaultHref}
                        />
                    )}

                    { buttonLeft === 'menu' && <IonMenuButton /> }
                </IonButtons>

                <Hide
                    fallback={
                        isModal
                            ? (
                                <IonTitle
                                    className='ion-text-center'
                                >
                                    { title ?? (
                                        <IonSkeletonText
                                            className='header__skeleton'
                                        />
                                    ) }
                                </IonTitle>
                            ) : (
                                <DesktopMenu />
                            )
                    }
                    on='desktop'
                >
                    <IonTitle
                        className='ion-text-center'
                    >
                        { title ?? (
                            <IonSkeletonText
                                className='header__skeleton'
                            />
                        ) }
                    </IonTitle>
                </Hide>

                <IonButtons
                    className='header__btn'
                    collapse
                    slot='end'
                >
                    { children }

                    {/*
                      * Hier stand ein Brief-Knopf in den Posteingang. Er war ein
                      * Notbehelf, solange der Chat keinen Platz in der
                      * Navigation hatte — seit es den Postfach-Reiter gibt,
                      * waere er ein zweiter Weg zum selben Ort und ein zweites
                      * Abzeichen fuer dieselbe Zahl.
                      */}
                </IonButtons>
                {visible && (
                    <IonProgressBar
                        type='indeterminate'
                        color='secondary'
                    />
                )}
            </IonToolbar>
        </IonHeader>
    )
}

export default Header
